import type {
    IActiveFinding,
    ICreativeIntelligencePayload,
    IEmergingSignal,
    IPooledComparison,
    IVariantArm,
} from '@/features/creative-intelligence/creative-intelligence-types'

/** Presentational helpers for the Creative Intelligence report. Pure — no rendering, no clock reads. */

/** Micros of the account currency → a compact money string (33_400_000_000 USD → "$33.4K"). */
export function formatSpend(micros: number, currency: string): string {
    const major = micros / 1_000_000
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: Math.abs(major) >= 1_000 ? 1 : 0,
            notation: Math.abs(major) >= 1_000 ? 'compact' : 'standard',
        }).format(major)
    } catch {
        return `${Math.round(major).toLocaleString()} ${currency}`
    }
}

/** A facet key as a readable label ("price_shown" → "Price shown"). Fallback when the curated one is absent. */
export function humanizeFacet(facet: string): string {
    const spaced = facet.replace(/_/g, ' ')
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** A variant's observed value as a label. Binary values read as Yes/No; anything else is title-cased. */
export function formatVariantValue(value: string): string {
    if (value === 'true') return 'Yes'
    if (value === 'false') return 'No'
    return humanizeFacet(value)
}

/**
 * The stratified effect, signed. A difference in the objective's ratio — a ROAS difference under
 * `roas`, conversions per unit of spend under `cvr` (the warehouse denominator is MICROS, so the raw
 * number is per-micro and must be scaled to be readable per currency unit).
 */
export function formatDelta(delta: number, objective: 'roas' | 'cvr'): string {
    const scaled = objective === 'roas' ? delta : delta * 1_000_000
    const sign = scaled > 0 ? '+' : ''
    return objective === 'roas' ? `${sign}${scaled.toFixed(2)} ROAS` : `${sign}${scaled.toFixed(2)} conv per unit spend`
}

export type Freshness = { state: 'never' | 'fresh' | 'stale'; label: string }

/**
 * Freshness from the serving artifact's computed-at + how far its facts run. Fresh within 48h of
 * compute; stale beyond — and stale SAYS stale, rather than relying on a change of emphasis. `now` is
 * injected for testability.
 */
export function computeFreshness(computedAt: string | null, factsThroughDate: string | null, now = Date.now()): Freshness {
    if (!computedAt) return { state: 'never', label: 'Never computed' }
    const ageHours = (now - new Date(computedAt).getTime()) / (1000 * 60 * 60)
    const facts = factsThroughDate ? ` · facts through ${factsThroughDate}` : ''
    const ago = ageHours < 1 ? 'just now' : ageHours < 24 ? `${Math.round(ageHours)}h ago` : `${Math.round(ageHours / 24)}d ago`
    if (ageHours <= 48) return { state: 'fresh', label: `Fresh · computed ${ago}${facts}` }
    return { state: 'stale', label: `Stale · computed ${ago}${facts}` }
}

/**
 * Span of the artifact's own window, in days. Used to tell whether a selected window differs from the
 * one the serving figures were actually computed over. `null` when either bound is unparseable.
 */
export function windowDaysOf(window: { since: string; until: string }): number | null {
    const since = Date.parse(`${window.since}T00:00:00Z`)
    const until = Date.parse(`${window.until}T00:00:00Z`)
    if (Number.isNaN(since) || Number.isNaN(until)) return null
    return Math.max(0, Math.round((until - since) / 86_400_000))
}

/** Share of window spend the analysis covers — analysed creative_daily spend / authoritative window spend. */
export function analysedSpendShare(analysedSpendMicros: number, windowSpendMicros?: number): number | null {
    if (!windowSpendMicros || windowSpendMicros <= 0) return null
    return Math.min(1, analysedSpendMicros / windowSpendMicros)
}

/** Cohort ROAS — a ratio of sums. `null` on zero spend; never fabricated as 0. */
export function variantRoas(v: IVariantArm): number | null {
    return v.spendMicros > 0 ? v.revenueMicros / v.spendMicros : null
}

/**
 * Cohort cost per purchase. The warehouse's `conversions` is Meta `omni_purchase` only, so this is a
 * cost per PURCHASE — never a cost per subscription, lead or signup; the column does not know what the
 * brand sells. `null` below the floor, because a two-purchase arm produces a meaningless figure.
 */
export const MIN_CONVERSIONS_FOR_CPA = 5
export function variantCostPerPurchase(v: IVariantArm): number | null {
    if (v.conversions < MIN_CONVERSIONS_FOR_CPA) return null
    return v.spendMicros / v.conversions
}

export interface IVariantView extends IVariantArm {
    label: string
    roas: number | null
    costPerPurchase: number | null
    isWinner: boolean
}

/**
 * The largest cohort ROAS across every finding — the single denominator the comparison bars scale to.
 * Bars share one scale across the whole panel so a stronger row reads as a longer bar; scaling each row
 * to its own best made every winner a full-width bar, so the winning side looked identical everywhere.
 */
export function panelRoasScale(findings: readonly IFindingView[]): number {
    let max = 0
    for (const f of findings) for (const v of f.variants) if (v.roas !== null && v.roas > max) max = v.roas
    return max
}

/** A cohort ROAS as a bar fraction in [0,1] against the panel-wide scale. `0` when unmeasured. */
export function variantBarFraction(roas: number | null, scaleMax: number): number {
    if (roas === null || scaleMax <= 0) return 0
    return Math.max(0, Math.min(1, roas / scaleMax))
}

export interface IFindingView {
    facet: string
    /** The curated parameter name where the vocabulary supplies one, else a humanized key. */
    label: string
    variants: IVariantView[]
    /** Winner and runner-up, present only for a two-variant comparison. */
    pair: { winner: IVariantView; loser: IVariantView } | null
    /**
     * Relative difference between the winner's and loser's RAW cohort ROAS, for a two-variant
     * comparison. Distinct from `delta` (within-campaign, stratified) and labelled as such — the two are
     * different statistics and can disagree in sign.
     */
    rawRelativeDelta: number | null
    /** The variant `delta` is oriented on (target minus pooled rest); `null` on pre-v2 artifacts. */
    targetValue: string | null
    delta: number
    rankScore: number
    minorityCreatives: number
    contributingStrata: number
    spendMicros: number
    droppedSpendShare: number
    largestStratumSpendShare: number
    /**
     * Benjamini–Hochberg adjusted p. `null` means NOT ESTIMABLE (too few campaigns ran both variants) —
     * it must never render as a q of 1, which would read as "tested, clearly null". `undefined` on
     * pre-v2 artifacts, which carried no q at all.
     */
    qValue: number | null | undefined
    /** False when too few campaigns ran both variants to estimate an effect. Defaults true pre-v3. */
    estimable: boolean
    /** Budget-confounded account-wide comparison; present only when most of the spend was excluded. */
    pooled?: IPooledComparison
    /** True when the artifact predates per-variant figures — the row renders with the delta only. */
    isLegacy: boolean
}

/**
 * Normalize a finding from either schema version into one view model, so no component branches on
 * `schemaVersion`. A v1 artifact is immutable and keeps serving until its brand recomputes, so it must
 * still render — it simply has no variants to compare, and `isLegacy` tells the row to say so.
 */
export function toFindingView(finding: IActiveFinding, labelFor: (facet: string) => string | undefined): IFindingView {
    const label = labelFor(finding.facet) ?? humanizeFacet(finding.facet)
    const arms = finding.variants ?? []

    const withRatios = arms.map((v) => ({ arm: v, roas: variantRoas(v) }))
    const best = withRatios.reduce<number | null>((acc, r) => (r.roas !== null && (acc === null || r.roas > acc) ? r.roas : acc), null)
    const worst = withRatios.reduce<number | null>((acc, r) => (r.roas !== null && (acc === null || r.roas < acc) ? r.roas : acc), null)

    const variants: IVariantView[] = withRatios.map(({ arm, roas }) => ({
        ...arm,
        label: formatVariantValue(arm.value),
        roas,
        costPerPurchase: variantCostPerPurchase(arm),
        isWinner: roas !== null && roas === best && best !== worst,
    }))

    const pair =
        variants.length === 2 && best !== null && worst !== null && best !== worst
            ? { winner: variants.find((v) => v.isWinner)!, loser: variants.find((v) => !v.isWinner)! }
            : null

    return {
        facet: finding.facet,
        label,
        variants,
        pair,
        rawRelativeDelta: pair && pair.loser.roas! > 0 ? pair.winner.roas! / pair.loser.roas! - 1 : null,
        targetValue: finding.targetValue ?? null,
        delta: finding.delta,
        rankScore: finding.rankScore,
        minorityCreatives: finding.minorityCreatives,
        contributingStrata: finding.contributingStrata,
        spendMicros: finding.spendMicros ?? arms.reduce((acc, v) => acc + v.spendMicros, 0),
        droppedSpendShare: finding.droppedSpendShare,
        largestStratumSpendShare: finding.largestStratumSpendShare,
        qValue: finding.qValue,
        // Pre-v3 artifacts carry no `estimable`; everything they surfaced had been tested, so true is
        // the correct degradation. A v3 non-estimable finding says so explicitly.
        estimable: finding.estimable ?? true,
        ...(finding.pooled ? { pooled: finding.pooled } : {}),
        isLegacy: arms.length === 0,
    }
}

export interface IBlueprintAxis {
    facet: string
    label: string
    /**
     * The axis's STANDING among this brand's own analysed axes, 0-100. This is a rank percentile, not a
     * quality score: there is no cross-brand anchor that would make an absolute rating meaningful.
     */
    standing: number
    /** 1-based rank by `rankScore`, and the population it is ranked within. */
    rank: number
    of: number
    /** The best-performing observed variant, where the artifact carries variants. */
    bestVariant?: IVariantView
    delta: number
    /** Below-gate axes are drawn explicitly insufficient — never at zero, never dropped. */
    insufficient: false
}

export interface IInsufficientAxis {
    facet: string
    label: string
    shortfall: { creatives: number; spendMicros: number }
    minorityCreatives: number
    insufficient: true
}

export type IAxis = IBlueprintAxis | IInsufficientAxis

/**
 * Radar radius band for scored axes, as a within-brand standing. Rank still orders the axes, but the
 * band is deliberately narrow and off the rim: mapping rank to the full 0–100 range pinned rank-1 to the
 * edge and dropped the last axis toward the centre, turning honest ordering into a spiky, one-sided
 * shape. A gentle [LO, HI] band keeps the blueprint a rounded, legible figure. Never shown as a number —
 * the surface states the rank in words (`formatStanding`), so the band is free to be chosen for the shape.
 */
const RADAR_STANDING_LO = 60
const RADAR_STANDING_HI = 95

/**
 * The variant a finding favours under the CAMPAIGN-ADJUSTED analysis, or `null` when the data cannot
 * orient it (a v1 artifact, or a facet run at three or more values). `delta` is oriented on `targetValue`
 * — a within-campaign, spend-weighted difference — so `delta ≥ 0` favours the target arm and `delta < 0`
 * favours the other. This is the conclusion the surface must lead with: the raw-ROAS winner is confounded
 * by which campaigns each arm ran in and can point the other way.
 */
export function adjustedWinnerVariant(f: IFindingView): IVariantView | null {
    if (f.variants.length !== 2 || f.targetValue === null) return null
    const target = f.variants.find((v) => v.value === f.targetValue)
    const other = f.variants.find((v) => v.value !== f.targetValue)
    if (!target || !other) return null
    return f.delta >= 0 ? target : other
}

/**
 * True when a row's raw-ROAS winner and its campaign-adjusted winner are DIFFERENT arms — the rare case
 * where confounding is strong enough to flip the sign. The win/lose bar colours are keyed to raw ROAS, so
 * on such a row a green "winner" bar points at the arm the trustworthy (adjusted) headline calls the
 * loser; the surface draws the bars neutral there instead, leaving the headline to state the conclusion.
 */
export function rawAdjustedDisagree(f: IFindingView): boolean {
    const adjusted = adjustedWinnerVariant(f)
    return f.pair !== null && adjusted !== null && adjusted.value !== f.pair.winner.value
}

/**
 * Seat the radar's scored axes so the strongest are not adjacent. `toBlueprintAxes` returns them in rank
 * order; drawn in that order the tall spokes cluster into one arc and pull the shape to one side.
 * Alternating the top half of the ranking with the bottom half spreads the peaks into a balanced,
 * rounded blueprint. Pure reordering — radius and labels are unchanged. Below-gate axes are NOT passed
 * here: sitting near the centre they would gash the polygon into a spiky star, so the surface lists them
 * beside the radar instead of plotting them.
 */
export function seatRadarAxes(axes: readonly IBlueprintAxis[]): IBlueprintAxis[] {
    const n = axes.length
    if (n <= 2) return [...axes]
    const half = Math.ceil(n / 2)
    const out: IBlueprintAxis[] = []
    for (let i = 0; i < half; i++) {
        out.push(axes[i]!)
        if (i + half < n) out.push(axes[i + half]!)
    }
    return out
}

/**
 * Blueprint axes: the scored facets ranked among THEMSELVES, plus the below-gate facets drawn as
 * explicit insufficient axes. The radius is a within-brand standing — `rank` of `of` — and never an
 * absolute score out of 100, which would imply a cross-brand standard the data cannot support.
 */
export function toBlueprintAxes(
    findings: IFindingView[],
    emerging: IEmergingSignal[],
    labelFor: (facet: string) => string | undefined,
    maxAxes = 10
): IAxis[] {
    const ranked = [...findings].sort((a, b) => b.rankScore - a.rankScore)
    const of = ranked.length
    const scored: IBlueprintAxis[] = ranked.slice(0, maxAxes).map((f, i) => ({
        facet: f.facet,
        label: f.label,
        // Rank ordered, radius within a gentle band off the rim. With a single axis there is nothing to
        // rank against, so it does not get a default 100th percentile.
        standing:
            of <= 1 ? 50 : Math.round(RADAR_STANDING_HI - (i / (of - 1)) * (RADAR_STANDING_HI - RADAR_STANDING_LO)),
        rank: i + 1,
        of,
        // The adjusted winner; the raw-ROAS winner only where a two-arm delta cannot orient it.
        bestVariant: adjustedWinnerVariant(f) ?? f.variants.find((v) => v.isWinner),
        delta: f.delta,
        insufficient: false,
    }))

    const remaining = Math.max(0, maxAxes - scored.length)
    const insufficient: IInsufficientAxis[] = emerging.slice(0, remaining).map((e) => ({
        facet: e.facet,
        label: labelFor(e.facet) ?? humanizeFacet(e.facet),
        shortfall: e.shortfall,
        minorityCreatives: e.minorityCreatives,
        insufficient: true,
    }))

    return [...scored, ...insufficient]
}

/** How an axis's standing reads in words. Never "N/100". */
export function formatStanding(axis: IBlueprintAxis): string {
    const ordinal = axis.rank === 1 ? '1st' : axis.rank === 2 ? '2nd' : axis.rank === 3 ? '3rd' : `${axis.rank}th`
    return `${ordinal} of ${axis.of} analysed ${axis.of === 1 ? 'axis' : 'axes'}`
}

/**
 * The header's ONE reconciling statement. Analysed and total are never presented as two unrelated
 * figures — the sentence names what excluded the difference.
 */
export function reconcileCounts(payload: ICreativeIntelligencePayload): string {
    const provider = payload.coverage.providers[0]
    const total = provider?.creatives ?? 0
    const analysed = provider?.taggedCreatives ?? 0
    if (total === 0) return 'No creatives in this window'
    if (analysed >= total) return `${analysed.toLocaleString()} creatives analysed`
    const excluded = total - analysed
    return `${analysed.toLocaleString()} of ${total.toLocaleString()} creatives analysed — ${excluded.toLocaleString()} could not be profiled`
}

/**
 * Why the measured-parameter count exceeds the confirmed-finding count. From v3 the tiers ARE a
 * partition — every measured parameter is rendered somewhere — so this reads as coverage rather than as
 * an unexplained gap. (Pre-v3 artifacts genuinely dropped parameters that reached no tier; the sentence
 * degrades to the old two-number form for them, since their payload cannot account for the difference.)
 */
export function reconcileParameters(payload: ICreativeIntelligencePayload): string {
    const { parametersConsidered, active, directional, measured, emerging, untested } = payload.counts
    if (parametersConsidered <= active) return `${parametersConsidered} parameters measured`
    if (measured === undefined) return `${parametersConsidered} parameters measured, ${active} discriminated performance`
    // EVERY tier, so the parts sum to the headline. Naming only the confirmed ones would restate the
    // unexplained gap this sentence exists to close.
    const parts = [
        `${active} confirmed`,
        directional ? `${directional} directional` : null,
        measured ? `${measured} no clear difference` : null,
        emerging ? `${emerging} watching` : null,
        untested ? `${untested} never varied` : null,
    ].filter(Boolean)
    return `${parametersConsidered} parameters measured · ${parts.join(' · ')}`
}
